import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../App";

const POLL_INTERVAL = 60000; // 60 seconds

export function useNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [settings, setSettings] = useState({
    enabled: true,
    advance_minutes: 15,
    sound: true,
  });
  const [upcoming, setUpcoming] = useState([]);
  const notifiedIds = useRef(new Set());
  const intervalRef = useRef(null);

  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get("/notifications/settings");
        setSettings(res.data);
      } catch (err) {
        // Use defaults
      }
    };
    fetchSettings();
  }, []);

  // Request browser permission
  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  // Check upcoming appointments
  const checkUpcoming = useCallback(async () => {
    if (!settings.enabled) return;
    try {
      const res = await api.get("/appointments/upcoming", {
        params: { minutes: settings.advance_minutes },
      });
      setUpcoming(res.data);

      // Send browser notifications for new upcoming appointments
      if (permission === "granted") {
        res.data.forEach((apt) => {
          if (!notifiedIds.current.has(apt.appointment_id)) {
            notifiedIds.current.add(apt.appointment_id);
            const typeLabels = {
              consultation: "Consulta",
              cleaning: "Limpeza",
              treatment: "Tratamento",
              surgery: "Cirurgia",
              checkup: "Revisão",
              emergency: "Emergência",
            };
            new Notification("ItapeviDents - Consulta Próxima", {
              body: `${apt.time} - ${apt.patient_name || "Paciente"} (${typeLabels[apt.type] || apt.type})`,
              icon: "/favicon.ico",
              tag: apt.appointment_id,
            });
          }
        });
      }
    } catch (err) {
      // Silent fail
    }
  }, [settings.enabled, settings.advance_minutes, permission]);

  // Polling
  useEffect(() => {
    if (!settings.enabled) return;
    checkUpcoming();
    intervalRef.current = setInterval(checkUpcoming, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [checkUpcoming, settings.enabled]);

  // Update settings
  const updateSettings = useCallback(async (newSettings) => {
    const merged = { ...settings, ...newSettings };
    setSettings(merged);
    try {
      await api.put("/notifications/settings", newSettings);
    } catch (err) {
      // Revert on failure
      setSettings(settings);
    }
  }, [settings]);

  // Clear notified cache daily
  useEffect(() => {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    const timeout = setTimeout(() => {
      notifiedIds.current.clear();
    }, midnight.getTime() - Date.now());
    return () => clearTimeout(timeout);
  }, []);

  return {
    permission,
    requestPermission,
    settings,
    updateSettings,
    upcoming,
    checkUpcoming,
  };
}
